import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, FileText, X, CheckCircle } from 'lucide-react';
import api from '../api/client';

export default function PolicyStatusWidget() {
  const [status, setStatus] = useState(null); // null = loading
  const [showModal, setShowModal] = useState(false);
  const [pendingAcks, setPendingAcks] = useState([]);
  const [confirmedPolicies, setConfirmedPolicies] = useState({});
  const [acknowledging, setAcknowledging] = useState(false);
  const [viewingPolicy, setViewingPolicy] = useState(null);

  const fetchStatus = useCallback(() => {
    api.get('/policies/my-status')
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ pending: [], acknowledged: [], total: 0 }));
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const openAcknowledge = () => {
    api.get('/policies/my-pending-acks')
      .then(r => {
        setPendingAcks(r.data || []);
        setConfirmedPolicies({});
        setShowModal(true);
      })
      .catch(() => {});
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await api.post('/policies/acknowledge-all');
      setShowModal(false);
      fetchStatus();
    } catch (e) {
      alert('Failed to save acknowledgement. Please try again.');
    } finally { setAcknowledging(false); }
  };

  const allConfirmed = pendingAcks.length > 0 && pendingAcks.every(p => confirmedPolicies[p.policy_id || p.id]);

  if (!status || status.total === 0) return null;

  const isPending = status.pending.length > 0;
  const latestAck = status.acknowledged.reduce((latest, a) =>
    !latest || new Date(a.acknowledged_at) > new Date(latest) ? a.acknowledged_at : latest, null);

  return (
    <>
      <style>{`
        @keyframes policyPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.4), 0 4px 24px rgba(0,0,0,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(245,158,11,0), 0 4px 24px rgba(0,0,0,0.4); }
        }
        @keyframes policyBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {isPending ? (
        <div
          onClick={openAcknowledge}
          style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(239,68,68,0.08))',
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 16,
            padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 14,
            cursor: 'pointer',
            animation: 'policyPulse 2s ease-in-out infinite',
            transition: 'transform 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.005)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldAlert size={20} style={{ color: '#f59e0b', animation: 'policyBlink 2s ease-in-out infinite' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fcd34d', marginBottom: 2 }}>
              IT Policy Acknowledgement Pending
            </div>
            <div style={{ fontSize: 11, color: '#92400e' }}>
              {status.pending.length} policy document{status.pending.length > 1 ? 's' : ''} require your acknowledgement — click to review and confirm
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '4px 10px', whiteSpace: 'nowrap' }}>
            Review & Acknowledge →
          </div>
        </div>
      ) : (
        <div style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(6,182,212,0.05))',
          border: '1px solid rgba(16,185,129,0.25)',
          borderRadius: 16,
          padding: '14px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheck size={20} style={{ color: '#34d399' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#34d399', marginBottom: 2 }}>
              IT Policies Acknowledged
            </div>
            <div style={{ fontSize: 11, color: '#065f46' }}>
              You are covered under {status.acknowledged.length} CL Synergy Group IT polic{status.acknowledged.length > 1 ? 'ies' : 'y'}
              {latestAck ? ` · Last acknowledged ${new Date(latestAck).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {status.acknowledged.map(p => (
              <span key={p.id} style={{ fontSize: 10, color: '#059669', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, padding: '2px 7px' }}>✓ {p.name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Acknowledgement Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ animation: 'fadeIn 0.2s ease-out' }}>
          <div className="absolute inset-0" style={{ background: 'rgba(4,8,16,0.92)', backdropFilter: 'blur(12px)' }} onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-2xl flex flex-col rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg,#0d1528,#080f1e)', border: '1px solid rgba(245,158,11,0.3)', maxHeight: '88vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(245,158,11,0.06)' }}>
              <div className="flex items-center gap-3">
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldAlert size={16} style={{ color: '#f59e0b' }} />
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: '#e2e8f0' }}>IT Policy Acknowledgement</div>
                  <div className="text-xs" style={{ color: '#475569' }}>Review and acknowledge your CL Synergy Group IT policies</div>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: 'none', cursor: 'pointer' }}>
                <X size={14} style={{ color: '#64748b' }} />
              </button>
            </div>

            {/* Policy list + PDFs */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {viewingPolicy ? (
                <div>
                  <button onClick={() => setViewingPolicy(null)} className="text-xs mb-3 flex items-center gap-1" style={{ color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer' }}>
                    ← Back to policy list
                  </button>
                  <iframe
                    src={`/api/policies/view/${viewingPolicy.policy_id || viewingPolicy.id}?token=${localStorage.getItem('token')}#toolbar=0&navpanes=0`}
                    style={{ width: '100%', height: 420, border: 'none', borderRadius: 12, background: '#fff' }}
                    title={viewingPolicy.policy_name || viewingPolicy.name}
                  />
                </div>
              ) : (
                <>
                  <div className="text-xs mb-2" style={{ color: '#475569' }}>
                    Please review each policy document and confirm you have read and understood its contents.
                  </div>
                  {pendingAcks.map(ack => {
                    const id = ack.policy_id || ack.id;
                    const name = ack.policy_name || ack.policy?.name || ack.name;
                    const confirmed = confirmedPolicies[id];
                    return (
                      <div key={id} style={{ background: confirmed ? 'rgba(16,185,129,0.07)' : 'rgba(255,255,255,0.03)', border: `1px solid ${confirmed ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 12, padding: '14px 16px' }}>
                        <div className="flex items-start gap-3">
                          <FileText size={16} style={{ color: confirmed ? '#34d399' : '#64748b', flexShrink: 0, marginTop: 1 }} />
                          <div style={{ flex: 1 }}>
                            <div className="text-sm font-medium" style={{ color: '#e2e8f0', marginBottom: 6 }}>{name}</div>
                            <button
                              onClick={() => setViewingPolicy(ack)}
                              style={{ fontSize: 11, color: '#60a5fa', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', marginBottom: 10 }}>
                              View Policy Document →
                            </button>
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!confirmed}
                                onChange={e => setConfirmedPolicies(prev => ({ ...prev, [id]: e.target.checked }))}
                                style={{ accentColor: '#10b981', marginTop: 1, flexShrink: 0 }}
                              />
                              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                I have read and understood the <strong style={{ color: '#e2e8f0' }}>{name}</strong> and agree to comply with its requirements.
                              </span>
                            </label>
                          </div>
                          {confirmed && <CheckCircle size={16} style={{ color: '#34d399', flexShrink: 0 }} />}
                        </div>
                      </div>
                    );
                  })}

                  <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', color: '#6366f1' }}>
                    After acknowledging, you can always refer to IT policies via My Portal → My Policies.
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {!viewingPolicy && (
              <div className="px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                  onClick={handleAcknowledge}
                  disabled={!allConfirmed || acknowledging}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  style={{ opacity: (!allConfirmed || acknowledging) ? 0.5 : 1, cursor: (!allConfirmed || acknowledging) ? 'not-allowed' : 'pointer' }}>
                  <ShieldCheck size={14} />
                  {acknowledging ? 'Saving...' : allConfirmed ? 'Acknowledge All Policies' : `Confirm all ${pendingAcks.length} polic${pendingAcks.length > 1 ? 'ies' : 'y'} above to continue`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
